/** Feefo group (canonical `mum9SwXP`) — the Feedback tab's review-request
 *  lifecycle. Xano is the source of truth for the request list; the tab reads it
 *  here (no per-load Feefo round-trip). Sends go through Xano-direct, which calls
 *  Feefo's EnterSaleRemotely; "Refresh" polls Feefo by order_reference for any
 *  un-responded rows. All three endpoints are admin-gated (staff bearer token
 *  attached automatically). The public /feefo/webhook is server-to-server only —
 *  never called from here. */

import { api } from "./client";
import type {
  FeefoRequest,
  FeefoRequestsResponse,
  FeefoCheckResponse,
  FeefoRequestInput,
  FeefoOpenItem,
  FeefoReceivedItem,
  FeefoJobSearchResult,
} from "@/types";

/** GET /feefo/requests/{job_id} — a job's request history (newest first) + summary. */
export function listFeefoRequests(jobId: number): Promise<FeefoRequestsResponse> {
  return api.get<FeefoRequestsResponse>("feefo", `feefo/requests/${jobId}`);
}

/** POST /feefo/request — send a new review invitation for the job's lead client.
 *  400 if no client email resolves; the created row comes back on success. */
export function createFeefoRequest(input: FeefoRequestInput): Promise<FeefoRequest> {
  return api.post<FeefoRequest>("feefo", "feefo/request", { body: input });
}

/** POST /feefo/check/{job_id} — poll Feefo for any un-responded rows and stamp
 *  reviews that have since arrived. Returns the refreshed list. */
export function checkFeefoRequests(jobId: number): Promise<FeefoCheckResponse> {
  return api.post<FeefoCheckResponse>("feefo", `feefo/check/${jobId}`);
}

/** GET /feefo/open — cross-job open requests (sent < 30 days ago, no response). */
export function listOpenFeedback(): Promise<{ items: FeefoOpenItem[] }> {
  return api.get<{ items: FeefoOpenItem[] }>("feefo", "feefo/open");
}

/** GET /feefo/received — cross-job responses to date, newest received first. */
export function listReceivedFeedback(): Promise<{ items: FeefoReceivedItem[] }> {
  return api.get<{ items: FeefoReceivedItem[] }>("feefo", "feefo/received");
}

/** GET /feefo/job_search?q= — job picker for the standalone New Request flow
 *  (min 2 chars; each match carries the resolved lead client + email). */
export function searchFeedbackJobs(q: string): Promise<{ items: FeefoJobSearchResult[] }> {
  return api.get<{ items: FeefoJobSearchResult[] }>("feefo", "feefo/job_search", { query: { q } });
}
